import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { useLocale } from '../../contexts/LocaleContext'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { DistributionStrip } from './DistributionStrip'
import { useBenchmarkLabels } from './useBenchmarkLabels'
import { formatGap, formatKPIValue, type Strip } from './benchmark-utils'

/**
 * The benchmark on a building's page: its three lines furthest from their
 * median, and the way to the full view (spec 2026-09-13-01). Nothing when the
 * building has no comparable line yet — an empty card is noise.
 */
interface Props {
    org: string
    assetUid: string
}

export function AssetBenchmarkCard({ org, assetUid }: Props) {
    const { t } = useTranslation('benchmark')
    const { locale } = useLocale()
    const { byCode, tradeLabel, kpiLabel } = useBenchmarkLabels(org)
    const query = $api.useQuery('get', '/api/v1/organizations/{orgCode}/benchmark/assets/{assetUid}', {
        params: { path: { orgCode: org, assetUid }, query: {} },
    })

    const strips = (query.data?.strips ?? [])
        .filter((s): s is Strip & { delta_vs_median: number } => s.delta_vs_median !== null && s.value !== null)
        .sort((a, b) => Math.abs(b.delta_vs_median) - Math.abs(a.delta_vs_median))
        .slice(0, 3)
    if (strips.length === 0) return null

    return (
        <Card data-testid="asset-benchmark-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-base">{t('asset_card.title')}</CardTitle>
                <Link
                    to="/$org/benchmark"
                    params={{ org }}
                    search={{ view: 'asset', asset: assetUid, metric: undefined, key: undefined, window_end: undefined, strata: undefined, currency: undefined }}
                    className="text-sm text-primary hover:underline"
                    data-testid="asset-benchmark-open"
                >
                    {t('asset_card.open')}
                </Link>
            </CardHeader>
            <CardContent className="space-y-1">
                {strips.map((s) => {
                    const unit = byCode.get(s.kpi_code)?.unit ?? 'currency'
                    const fmt = (v: number) => formatKPIValue(unit, v, s.currency, locale)
                    const title = s.breakdown_key ? tradeLabel(s.breakdown_key) : kpiLabel(s.kpi_code)
                    return (
                        <div key={`${s.kpi_code}|${s.breakdown_key}|${s.subject.uid}`} className="grid grid-cols-[minmax(0,10rem)_minmax(0,1fr)_auto] items-center gap-3">
                            <span className="truncate text-sm text-foreground" title={title}>{title}</span>
                            <DistributionStrip
                                value={s.value}
                                stats={s.cohort.suppressed ? null : (s.stats ?? null)}
                                peerValues={s.peer_values}
                                outlier={s.outlier}
                                label={title}
                                formatValue={fmt}
                                testId={`asset-benchmark-card-${s.kpi_code}${s.breakdown_key ? `-${s.breakdown_key}` : ''}`}
                            />
                            <span className={`text-sm tabular-nums ${s.outlier === 'high' ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                                {formatGap(s.delta_vs_median, locale)}
                            </span>
                        </div>
                    )
                })}
            </CardContent>
        </Card>
    )
}
